import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';

import { formatAmountValue, sqlDateToLabel, fractionToPercent } from 'helpers/helpers';
import { rentIsZero } from 'helpers/targets';
import RetirementIcon from 'components/Icons/RetirementIcon';

import BarGraph from 'components/Graph/BarGraph';
import { CalculationMode } from 'common/interfaces';
import { RentData } from 'stores/workflowStores/finances/rent/model';
import useGeneralStyles from 'apps/Pdf/styles/general';
import useStyles from './TargetsStyle';
import { PersonType, Currency, EdwardColors } from 'enums';

interface Props {
  currency: Currency;
  data?: RentData;
  personType: PersonType;
  annual?: boolean;
  actualInflation: number;
  original?: boolean;
}

const Targets: React.FC<Props> = ({ data, currency, personType, annual, actualInflation, original }) => {
  const { t } = useTranslation();
  const styles = useGeneralStyles();
  const classes = useStyles();
  const translatedCurrency = useMemo(() => t(`currency_${currency}`), [t, currency]);
  const zeroRent = rentIsZero(data);

  return data ? (
    <>
      <Grid container item alignContent="center" alignItems="center" className={`${classes.target}`}>
        <Grid item xs={1} className={classes.icon}>
          <RetirementIcon fontSize="inherit" htmlColor={original ? EdwardColors.GRAY_MEDIUM : undefined} />
        </Grid>
        <Grid item xs={annual && data.inflation.valuation ? 9 : 7}>
          <span className={styles.mediumText}>Pravidelná renta z portfolia</span>
          <br />
          {zeroRent ? (
            t('additionalTargets_zeroRent')
          ) : (
            <>
              {data.portfolioRentCalculationMode === CalculationMode.CALCULATED && 'Námi vypočítaná výše měsíční renty'}
              {data.portfolioRentCalculationMode === CalculationMode.MANUAL && 'Zákazníkem zvolená výše měsíční renty'}
              {data.portfolioRentCalculationMode === CalculationMode.REQUIRED && 'Námi vypočítaná výše měsíční renty'}
              {' | '}
              {t('general_since')}
              {': '}
              {sqlDateToLabel(data.start)}
              {data.inflation?.enabled && <> | plánovaná inflace: {fractionToPercent(data.inflation.rate)}</>}
            </>
          )}
        </Grid>
        {!zeroRent && (
          <>
            {!annual && !data.inflation.valuation && (
              <Grid item xs={2}>
                <span className={styles.mediumText}>
                  {data.portfolioRentCalculationMode === CalculationMode.CALCULATED &&
                    formatAmountValue(data.calculatedPortfolioRent.value, currency)}
                  {data.portfolioRentCalculationMode === CalculationMode.MANUAL &&
                    formatAmountValue(data.plannedPortfolioRent.value, currency)}
                  {data.portfolioRentCalculationMode === CalculationMode.REQUIRED &&
                    formatAmountValue(data.requiredPortfolioRent.value, currency)}
                </span>
                <br />
                {t('general_frequency_MONTHLY')}
              </Grid>
            )}
            <Grid item xs={2}>
              {data.durabilityUntil ? (
                personType === PersonType.PHYSICAL ? (
                  <>{` ${t('general_Until')}: ${data.durability.years} ${t('general_years')}`}</>
                ) : (
                  <>
                    {` ${t('general_Until')}: `}
                    {sqlDateToLabel(data.durabilityUntil)}
                  </>
                )
              ) : (
                <> {t(`general_allTime${personType === PersonType.LEGAL ? '_legal' : ''}`)}</>
              )}
            </Grid>
          </>
        )}

        {annual && data.inflation.valuation && (
          <>
            <Grid item xs={12} className={`${styles.bold} ${styles.regularText} ${classes.valuation}`}>
              Částka cíle se automaticky navyšuje podle {data.inflation.type === 'ACTUAL' ? 'skutečné' : 'nastavené'}{' '}
              inflace {fractionToPercent(data.inflation.type === 'PLANNED' ? data.inflation.rate : actualInflation)} na{' '}
              <span className={styles.mediumText}>
                {data.portfolioRentCalculationMode === CalculationMode.CALCULATED &&
                  formatAmountValue(data.calculatedPortfolioRent.value, currency)}
                {data.portfolioRentCalculationMode === CalculationMode.MANUAL &&
                  formatAmountValue(data.plannedPortfolioRent.value, currency)}
                {data.portfolioRentCalculationMode === CalculationMode.REQUIRED &&
                  formatAmountValue(data.requiredPortfolioRent.value, currency)}
              </span>
              {t('general_frequency_MONTHLY')}
            </Grid>
          </>
        )}

        {data.portfolioRentCalculationMode !== CalculationMode.MANUAL && (
          <Grid item xs={12} className={classes.chart}>
            <BarGraph
              data={data}
              plan={true}
              currency={currency}
              translatedCurrency={translatedCurrency}
              original={original}
              settings={{
                chart: { width: 716, height: 40, marginRight: 0, animation: false },
                plotOptions: {
                  series: {
                    animation: false,
                    enableMouseTracking: false,
                  },
                },
              }}
            />
          </Grid>
        )}
      </Grid>
    </>
  ) : null;
};

export default Targets;
